import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSEO } from "../components/SEO";
import { Reveal, MaskedLines } from "../components/AnimatedSection";
import { ProjectCard } from "../components/ProjectCard";
import { projects, projectCategories } from "../data/projects";

const Project = () => {
  const [filter, setFilter] = useState("All");
  useSEO(
    "Project | Social Wavez Technologies",
    "A selection of websites, platforms, CRMs and automations we've built for premium businesses.",
  );

  const filtered =
    filter === "All" ? projects : projects.filter((p) => p.category === filter);

  return (
    <>
      <section className="bg-deep relative overflow-hidden pb-20 pt-40 md:pb-28 md:pt-52">
        <div className="bg-grid-dark absolute inset-0 opacity-50" />
        <div className="animate-float-slow pointer-events-none absolute -left-24 top-20 h-80 w-80 rounded-full bg-[#3CBE6E]/15 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-white/50">
              <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-[#3CBE6E] to-[#D3B74D]" />
              Our Work
            </p>
          </Reveal>
          <h1 className="mt-7 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLines
              trigger="mount"
              delay={0.25}
              lines={[
                "Work That Solves",
                <span key="pj2" className="text-gradient-pulse">Real Problems.</span>,
              ]}
            />
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-white/60 md:text-lg">
              A selection of websites, platforms, CRMs and automations built
              for premium businesses — each one designed around a real business
              problem, not a template.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F8FBF6] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <div className="flex flex-wrap gap-2.5" role="tablist" aria-label="Filter projects">
              {projectCategories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  role="tab"
                  aria-selected={filter === cat}
                  data-testid={`filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  onClick={() => setFilter(cat)}
                  className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                    filter === cat
                      ? "border-transparent bg-gradient-to-r from-[#1C7543] to-[#D3B74D] text-white shadow-[0_8px_24px_rgba(28,117,67,0.35)]"
                      : "border-[#07130C]/15 bg-white text-[#07130C]/60 hover:border-[#1C7543]/40 hover:text-[#07130C]"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </Reveal>

          <motion.div layout className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {filtered.map((p) => (
                <motion.div
                  layout
                  key={p.slug}
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.94 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  <ProjectCard project={p} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Project;
